// src/components/Footer.jsx
import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        color: 'common.white',
        pt: { xs: 6, md: 8 },
        pb: 0,
        // Layered gradient with subtle texture
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        overflow: 'hidden',
      }}
    >
      {/* Decorative wave */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -60,
          left: 0,
          right: 0,
          height: 120,
          background:
            'radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,.12), rgba(0,0,0,0) 60%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Top CTA strip */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,.08)',
            border: '1px solid rgba(255,255,255,.15)',
            borderRadius: 3,
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
            mb: { xs: 4, md: 6 },
            boxShadow: '0 12px 28px rgba(0,0,0,.18)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 900, lineHeight: 1.15, mb: 0.5 }}
              >
                Move with local experts
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                MRM Lettings Property &amp; Management Ltd — one dedicated Middlesbrough office,
                local knowledge, trusted since 2019.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                component={RouterLink}
                to="/sellers/valuation"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: 800, borderRadius: 999 }}
              >
                Free Valuation
              </Button>
              <Button
                component={RouterLink}
                to="/repairs"
                variant="outlined"
                color="inherit"
                sx={{ fontWeight: 800, borderRadius: 999, borderColor: 'rgba(255,255,255,.6)' }}
              >
                Report a Repair
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Main footer grid */}
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Brand + rating + socials */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
              MRM Lettings
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.95 }}>
              Property &amp; Management Ltd
              <br />
              Local experts. Clear advice. Real results.
            </Typography>

            {/* Review badge */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon fontSize="small" />
                <StarIcon fontSize="small" />
                <StarIcon fontSize="small" />
                <StarIcon fontSize="small" />
                <StarHalfIcon fontSize="small" />
              </Box>
              <Chip
                label="4.74 / 5"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,.15)',
                  color: 'inherit',
                  border: '1px solid rgba(255,255,255,.25)',
                  fontWeight: 800,
                }}
              />
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                1,571 Google reviews
              </Typography>
            </Stack>

            {/* Socials */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <IconButton
                component="a"
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                sx={{ color: 'common.white' }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                sx={{ color: 'common.white' }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{ color: 'common.white' }}
              >
                <LinkedInIcon />
              </IconButton>
            </Stack>

            {/* Mini badges */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="No sale, no fee"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,.12)', color: 'inherit' }}
              />
              <Chip
                label="ARLA-trained team"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,.12)', color: 'inherit' }}
              />
              <Chip
                label="Local market expertise"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,.12)', color: 'inherit' }}
              />
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Quick Links
            </Typography>
            {['Home', 'Buyers', 'Sellers', 'Landlords', 'Tenants', 'About', 'Contact'].map(
              (text) => (
                <MuiLink
                  key={text}
                  component={RouterLink}
                  to={text === 'Home' ? '/' : `/${text.toLowerCase()}`}
                  color="inherit"
                  underline="hover"
                  sx={{ display: 'block', py: 0.5, opacity: 0.95 }}
                >
                  {text}
                </MuiLink>
              ),
            )}
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Services
            </Typography>
            {[
              { label: 'Book Valuation', to: '/sellers/valuation' },
              { label: 'Property Alerts', to: '/sellers/alerts' },
              { label: 'Report a Repair', to: '/repairs' },
              { label: 'Meet the Team', to: '/about#team' },
            ].map((item) => (
              <MuiLink
                key={item.label}
                component={RouterLink}
                to={item.to}
                color="inherit"
                underline="hover"
                sx={{ display: 'block', py: 0.5, opacity: 0.95 }}
              >
                {item.label}
              </MuiLink>
            ))}
          </Grid>

          {/* Contact + Registered office */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Contact Us
            </Typography>
            <Stack spacing={1.2}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  Registered office:
                  <br />
                  142 Victoria Road, Middlesbrough, England, TS1 3HU
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon fontSize="small" />
                <MuiLink
                  href="mailto:info@mrmlettings.co.uk"
                  color="inherit"
                  underline="hover"
                  variant="body2"
                >
                  info@mrmlettings.co.uk
                </MuiLink>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PhoneIcon fontSize="small" />
                <MuiLink href="tel:+441234567890" color="inherit" underline="hover" variant="body2">
                  01642243378
                </MuiLink>
              </Stack>
            </Stack>

            {/* Small company facts */}
            <Box sx={{ mt: 2, opacity: 0.9 }}>
              <Typography variant="caption" display="block">
                Incorporated on 15 April 2019 · Status: Active
              </Typography>
              <Typography variant="caption" display="block">
                SIC 68320 — Management of real estate on a fee or contract basis
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Legal / bottom bar */}
        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: 'rgba(255,255,255,.25)' }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ pb: 6 }}
        >
          <Typography variant="body2" sx={{ opacity: 0.95 }}>
            © {year} MRM Lettings Property &amp; Management Ltd. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <MuiLink component={RouterLink} to="/privacy" color="inherit" underline="hover" variant="body2">
              Privacy
            </MuiLink>
            <MuiLink component={RouterLink} to="/terms" color="inherit" underline="hover" variant="body2">
              Terms
            </MuiLink>
            <MuiLink component={RouterLink} to="/cookies" color="inherit" underline="hover" variant="body2">
              Cookies
            </MuiLink>
          </Stack>

          <IconButton
            aria-label="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              ml: { xs: 0, md: 2 },
              color: 'common.white',
              border: '1px solid rgba(255,255,255,.5)',
              bgcolor: 'rgba(255,255,255,.08)',
              '&:hover': { bgcolor: 'rgba(255,255,255,.15)' },
            }}
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Stack>
      </Container>

      {/* JSON-LD Organization schema (basic) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'MRM Lettings Property & Management Ltd',
            url: typeof window !== 'undefined' ? window.location.origin : undefined,
            email: 'info@mrmlettings.co.uk',
            telephone: '+441234567890',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '142 Victoria Road',
              addressLocality: 'Middlesbrough',
              addressCountry: 'GB',
              postalCode: 'TS1 3HU',
            },
            sameAs: [
              'https://facebook.com',
              'https://instagram.com',
              'https://linkedin.com',
            ],
          }),
        }}
      />
    </Box>
  );
}
